/**
 * HelpCategoriesTab
 * Admin management table for Help categories: create/edit via GenericModal +
 * CategoryForm, delete via ConfirmActionModal. Deletion is blocked (disabled
 * action, no modal opened) while the category still has articles assigned,
 * mirroring the disabled-delete-button precedent used by
 * DivisionManagementPage for divisions with dependents.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2 } from 'lucide-react';
import type { UseFormSetError } from 'react-hook-form';
import { helpService } from '@services';
import { GenericModal } from '@components/common/modals';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import Button from '@components/common/Button';
import { showToast } from '@utils/toast';
import { getApiErrorMessage, getApiErrorStatus } from '@utils/apiError';
import type { HelpCategory, CreateHelpCategoryPayload, UpdateHelpCategoryPayload } from '@app-types';
import CategoryForm, { type CategoryFormValues } from './CategoryForm';
import styles from './HelpCategoriesTab.module.css';

const HTTP_CONFLICT = 409;

const HelpCategoriesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const editFormRef = useRef<HTMLFormElement>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);

  // Held in refs (not state) so handing setError to the parent never triggers
  // a re-render of the form that produced it.
  const createSetErrorRef = useRef<UseFormSetError<CategoryFormValues> | null>(null);
  const editSetErrorRef = useRef<UseFormSetError<CategoryFormValues> | null>(null);

  const handleCreateSetErrorReady = useCallback((setError: UseFormSetError<CategoryFormValues>) => {
    createSetErrorRef.current = setError;
  }, []);

  const handleEditSetErrorReady = useCallback((setError: UseFormSetError<CategoryFormValues>) => {
    editSetErrorRef.current = setError;
  }, []);

  /**
   * Surfaces the failure as a toast and, for a 409 duplicate name, also pins it
   * to the name field so the still-open modal shows what needs changing.
   */
  const handleMutationError = (
    error: Error,
    fallback: string,
    setError: UseFormSetError<CategoryFormValues> | null
  ) => {
    const message = getApiErrorMessage(error, fallback);
    showToast.error(message);
    if (getApiErrorStatus(error) === HTTP_CONFLICT) {
      setError?.('name', { type: 'server', message });
    }
  };

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['help-admin-categories'],
    queryFn: () => helpService.getAdminCategories()
  });

  const { data: articles = [] } = useQuery({
    queryKey: ['help-admin-articles'],
    queryFn: () => helpService.getAdminArticles()
  });

  const articleCountByCategory = useMemo(() => {
    const counts = new Map<number, number>();
    articles.forEach((article) => {
      counts.set(article.category_id, (counts.get(article.category_id) ?? 0) + 1);
    });
    return counts;
  }, [articles]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order),
    [categories]
  );

  const nextSortOrder = useMemo(
    () => (categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order)) + 1 : 1),
    [categories]
  );

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: ['help-admin-categories'] });
    // category_name is denormalized onto article responses, so a rename leaves
    // stale headings on the articles views until these are refetched too.
    queryClient.invalidateQueries({ queryKey: ['help-admin-articles'] });
    // The reader queries (Help Center tab, HelpArticlePage) have no observer
    // while this tab is open, and the app disables refetch-on-mount, so a plain
    // invalidation would leave them serving pre-edit data until a reload.
    // refetchType 'all' refetches them now, inactive or not.
    queryClient.invalidateQueries({ queryKey: ['help-categories'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['help-articles'], refetchType: 'all' });
    // Prefix match: covers every cached ['help-article', id] detail.
    queryClient.invalidateQueries({ queryKey: ['help-article'], refetchType: 'all' });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateHelpCategoryPayload) => helpService.createCategory(data),
    onSuccess: () => {
      showToast.success('Category created successfully');
      invalidateCategories();
      setIsCreateModalOpen(false);
    },
    onError: (error: Error) => {
      handleMutationError(error, 'Failed to create category', createSetErrorRef.current);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHelpCategoryPayload }) => helpService.updateCategory(id, data),
    onSuccess: () => {
      showToast.success('Category updated successfully');
      invalidateCategories();
      setIsEditModalOpen(false);
    },
    onError: (error: Error) => {
      handleMutationError(error, 'Failed to update category', editSetErrorRef.current);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => helpService.deleteCategory(id),
    onSuccess: () => {
      showToast.success('Category deleted successfully');
      invalidateCategories();
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
    },
    onError: (error: Error) => {
      showToast.error(getApiErrorMessage(error, 'Failed to delete category'));
    }
  });

  const handleCreate = (data: CreateHelpCategoryPayload | UpdateHelpCategoryPayload) => {
    createMutation.mutate(data as CreateHelpCategoryPayload);
  };

  const handleUpdate = (data: CreateHelpCategoryPayload | UpdateHelpCategoryPayload) => {
    if (!selectedCategory) return;
    updateMutation.mutate({ id: selectedCategory.id, data });
  };

  const handleDelete = () => {
    if (!selectedCategory) return;
    deleteMutation.mutate(selectedCategory.id);
  };

  if (categoriesLoading) {
    return <div className={styles.loadingState}>Loading categories...</div>;
  }

  return (
    <div className={styles.categoriesTab} data-test-id="help-categories-tab">
      <div className={styles.header}>
        <Button variant="primary" size="sm" onClick={() => setIsCreateModalOpen(true)} data-test-id="add-category-button">
          <Plus size={16} />
          Add Category
        </Button>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableWrapper}>
          <table className={styles.table} data-test-id="help-categories-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Articles</th>
                <th>Sort Order</th>
                <th className={styles.actionsColumn}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.emptyState}>
                    No categories found
                  </td>
                </tr>
              ) : (
            sortedCategories.map((category) => {
              const articleCount = articleCountByCategory.get(category.id) ?? 0;
              const hasArticles = articleCount > 0;

              return (
                <tr key={category.id} data-test-id={`category-row-${category.id}`}>
                  <td className={styles.categoryName}>{category.name}</td>
                  <td>
                    <span className={styles.countBadge}>{articleCount}</span>
                  </td>
                  <td className={styles.mutedCell}>{category.sort_order}</td>
                  <td className={styles.actionsColumn}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedCategory(category);
                          setIsEditModalOpen(true);
                        }}
                        title="Edit"
                        data-test-id="action-button-edit"
                      >
                        <Edit size={16} color="#2563eb" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedCategory(category);
                          setIsDeleteModalOpen(true);
                        }}
                        title={hasArticles ? `Cannot delete: ${articleCount} article(s) assigned` : 'Delete'}
                        disabled={hasArticles}
                        data-test-id="action-button-delete"
                      >
                        <Trash2 size={16} color={hasArticles ? '#d1d5db' : '#dc2626'} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
            </tbody>
          </table>
        </div>
      </div>

      <GenericModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Category"
        disableBackdropClick
        dataTestId="create-category-modal"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => editFormRef.current?.requestSubmit()}
              loading={createMutation.isPending}
              disabled={createMutation.isPending}
              data-test-id="submit-create-category"
            >
              Create Category
            </Button>
          </>
        }
      >
        <CategoryForm
          defaultSortOrder={nextSortOrder}
          onSubmit={handleCreate}
          formRef={editFormRef}
          onSetErrorReady={handleCreateSetErrorReady}
        />
      </GenericModal>

      <GenericModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Category"
        disableBackdropClick
        dataTestId="edit-category-modal"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => editFormRef.current?.requestSubmit()}
              loading={updateMutation.isPending}
              disabled={updateMutation.isPending}
              data-test-id="submit-edit-category"
            >
              Save Changes
            </Button>
          </>
        }
      >
        {selectedCategory && (
          <CategoryForm
            category={selectedCategory}
            defaultSortOrder={selectedCategory.sort_order}
            onSubmit={handleUpdate}
            formRef={editFormRef}
            onSetErrorReady={handleEditSetErrorReady}
          />
        )}
      </GenericModal>

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to permanently delete "${selectedCategory?.name}"? This action cannot be undone.`}
        isSubmitting={deleteMutation.isPending}
        actionType="delete"
      />
    </div>
  );
};

export default HelpCategoriesTab;
